import {
  ActionType,
  ComplianceItemStatus,
  FormType,
  HomePageType,
  ListVisibility,
  TimeUnit,
  TriggerType,
} from "~/api";

type ElTagType = "primary" | "success" | "danger" | "info" | "warning";

export type EnumDescription = {
  label: string;
  description?: string;
  tagType?: ElTagType;
  bgColor?: string;
  fontColor?: string;
};

export type EnumDescriptionDict<T extends number | string> = {
  [K in T]: K extends T ? EnumDescription : never;
};

export const complianceItemStatusDescriptions: EnumDescriptionDict<ComplianceItemStatus> =
  {
    [ComplianceItemStatus.ACTIVE]: {
      label: "Active",
      tagType: "success",
    },
    [ComplianceItemStatus.IN_USE]: {
      label: "In Use",
      tagType: "primary",
    },
    [ComplianceItemStatus.ARCHIVED]: {
      label: "Archived",
      tagType: "info",
    },
    [ComplianceItemStatus.INACTIVE]: {
      label: "Inactive",
      tagType: "danger",
    },
    [ComplianceItemStatus.UNUSED]: {
      label: "Unused",
      tagType: "info",
    },
  };

export const complianceItemTriggerTypeDescriptions: EnumDescriptionDict<TriggerType> =
  {
    [TriggerType.BEFORE]: {
      label: "Before",
      tagType: "success",
    },
    [TriggerType.AFTER]: {
      label: "After",
      tagType: "success",
    },
    // [TriggerType.RECURRING]: {
    //   label: "Every",
    //   tagType: "success",
    // },
  };

export const complianceItemTriggerActionDescriptions: EnumDescriptionDict<ActionType> =
  {
    [ActionType.EMAIL]: {
      label: "Email",
      tagType: "success",
    },
    [ActionType.SMS]: {
      label: "SMS",
      tagType: "success",
    },
    [ActionType.CALL]: {
      label: "Call",
      tagType: "success",
    },
    [ActionType.NOTIFICATION]: {
      label: "Notification",
      tagType: "success",
    },
    [ActionType.CHANGE_WORKFLOW_STATUS]: {
      label: "Change Workflow Status",
      tagType: "success",
    },
  };

export const complianceItemTriggerTimeUnitDescriptions: EnumDescriptionDict<TimeUnit> =
  {
    [TimeUnit.DAY]: {
      label: "Day",
      tagType: "success",
    },
    [TimeUnit.WEEK]: {
      label: "Week",
      tagType: "success",
    },
    [TimeUnit.MONTH]: {
      label: "Month",
      tagType: "success",
    },
    [TimeUnit.YEAR]: {
      label: "Year",
      tagType: "success",
    },
  };

export const formTypeDescriptions: EnumDescriptionDict<FormType> = {
  [FormType.COMPLIANCE_ITEM_DEFINITION]: {
    label: "Compliance Item Definition",
    tagType: "success",
  },
  [FormType.WORKFLOW_DEFINITION_MAIN_FORM]: {
    label: "Standalone Form",
    tagType: "success",
  },
  [FormType.WORKFLOW_STEP]: {
    label: "Inline Form",
    tagType: "success",
  },
  [FormType.OTHER]: {
    label: "Other",
    description: undefined,
    tagType: undefined,
  },
  [FormType.ENTITY_DEFINITION]: {
    label: "Entity Definition",
    tagType: "success",
  },
  [FormType.EMAIL_TEMPLATE]: {
    label: "Email Template",
    tagType: "success",
  },
  [FormType.EMAIL_STYLE_TEMPLATE]: {
    label: "Email Style Template",
    tagType: "success",
  },
  [FormType.MAIN_APPLICATION]: {
    label: "",
    description: undefined,
    tagType: undefined,
    bgColor: undefined,
    fontColor: undefined,
  },
  [FormType.MEDICAL_FORMS]: {
    label: "",
    description: undefined,
    tagType: undefined,
    bgColor: undefined,
    fontColor: undefined,
  },
  [FormType.NEEDS_ASSESSMENT]: {
    label: "",
    description: undefined,
    tagType: undefined,
    bgColor: undefined,
    fontColor: undefined,
  },
  [FormType.APPLICATION_UPDATE_REQUEST]: {
    label: "",
    description: undefined,
    tagType: undefined,
    bgColor: undefined,
    fontColor: undefined,
  },
  [FormType.HMBINFORMATION_UPDATE_REQUEST]: {
    label: "",
    description: undefined,
    tagType: undefined,
    bgColor: undefined,
    fontColor: undefined,
  },
  [FormType.APPLICATION_NOTE]: {
    label: "",
    description: undefined,
    tagType: undefined,
    bgColor: undefined,
    fontColor: undefined,
  },
  [FormType.OFFER]: {
    label: "",
    description: undefined,
    tagType: undefined,
    bgColor: undefined,
    fontColor: undefined,
  },
  [FormType.NO_OFFER]: {
    label: "",
    description: undefined,
    tagType: undefined,
    bgColor: undefined,
    fontColor: undefined,
  },
  [FormType.INTAKE]: {
    label: "",
    description: undefined,
    tagType: undefined,
    bgColor: undefined,
    fontColor: undefined,
  },
  [FormType.IMPORTED_INFORMATION]: {
    label: "",
    description: undefined,
    tagType: undefined,
    bgColor: undefined,
    fontColor: undefined,
  },
  [FormType.CANCEL_APPLICATION]: {
    label: "",
    description: undefined,
    tagType: undefined,
    bgColor: undefined,
    fontColor: undefined,
  },
  [FormType.MOVE_BACK_TO_REVIEW]: {
    label: "",
    description: undefined,
    tagType: undefined,
    bgColor: undefined,
    fontColor: undefined,
  },
  [FormType.MOVE_BACK_TO_WAITLIST]: {
    label: "",
    description: undefined,
    tagType: undefined,
    bgColor: undefined,
    fontColor: undefined,
  },
  [FormType.CONFIGURED_FORM]: {
    label: "Configured Form",
    tagType: "success",
  },
};

export const homePageTypeDescriptions: EnumDescriptionDict<HomePageType> = {
  [HomePageType.DEFAULT]: {
    label: "Default",
    tagType: "success",
  },
  [HomePageType.WORKFLOWS]: {
    label: "Workflows",
    tagType: "success",
  },
};

export const listVisibilityDescriptions: EnumDescriptionDict<ListVisibility> = {
  [ListVisibility.PUBLIC]: {
    label: "Public",
  },
  [ListVisibility.ORG_WIDE]: {
    label: "SysOrg Wide",
  },
  [ListVisibility.WORKFLOW_SPECIFIC]: {
    label: "Workflow Specific",
  },
  [ListVisibility.FORM_SPECIFIC]: {
    label: "Form Specific",
  },
};

export const enumDescriptions: Record<string, any> = {
  complianceItemStatus: complianceItemStatusDescriptions,
  triggerType: complianceItemTriggerTypeDescriptions,
  timeUnit: complianceItemTriggerTimeUnitDescriptions,
  actionType: complianceItemTriggerActionDescriptions,
  homePageType: homePageTypeDescriptions,
  formType: formTypeDescriptions,
  customDescriptions: undefined,
};

/**
 * Register additional enum descriptions from external modules.
 * Called at import time by module-specific route files (e.g., YYC_Housing/router/index.ts).
 */
export function registerEnumDescriptions(
  descriptions: Record<string, EnumDescriptionDict<any>>,
) {
  Object.assign(enumDescriptions, descriptions);
}

export type CustomEnumDescriptions = Record<string | number, EnumDescription>;
